import { useState, useMemo, useContext, createContext } from 'react'
import Dialog from '@mui/material/Dialog'
import Box from '@mui/material/Box'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import useCallbackDebounced from '../../../hooks/shared/useCallbackDebounced'
import DialogSlideRightToLeftTransition from '../../atoms/DialogSlideRightToLeftTransition'
import { CompanyPaymentsPanel } from '../../features/companyPayments/CompanyPaymentsPanel'
import IconButton from '@mui/material/IconButton'
import { useOverlayPageNavigateBack } from '../../../hooks/navigation/usePersistedBackgroundLocation'

const CompanyPaymentsDialogContext = createContext<{
  openCompanyPaymentsDialog: () => void
  closeCompanyPaymentsDialog: () => void
} | null>(null)

const useCompanyPaymentsDialog = () => {
  const context = useContext(CompanyPaymentsDialogContext)
  if (!context) {
    throw new Error(
      'no CompanyPaymentsDialogProvider found when calling useCompanyPaymentsDialog()',
    )
  }
  return context
}

const CompanyPaymentsDialogProvider = ({ children }) => {
  const overlayPageNavigateBack = useOverlayPageNavigateBack()
  const [open, setOpen] = useState(false)
  const openCompanyPaymentsDialog = useCallbackDebounced(() => {
    setOpen(true)
  }, [])
  const closeCompanyPaymentsDialog = useCallbackDebounced(() => {
    setOpen(false)
  }, [])
  const onClose = useCallbackDebounced(() => {
    overlayPageNavigateBack()
  }, [overlayPageNavigateBack])
  const value = useMemo(
    () => ({
      openCompanyPaymentsDialog,
      closeCompanyPaymentsDialog,
    }),
    [closeCompanyPaymentsDialog, openCompanyPaymentsDialog],
  )
  return (
    <CompanyPaymentsDialogContext.Provider value={value}>
      {children}
      <Dialog
        open={open}
        slots={{
          transition: DialogSlideRightToLeftTransition,
        }}
        onClose={onClose}
        sx={theme => ({
          '& .MuiDialog-paper': {
            maxWidth: '50vw',
            height: '100vh',
            width: 1000,
            position: 'fixed',
            top: 0,
            right: 0,
            margin: 0,
            maxHeight: 'none',
            borderRadius: 0,
            backgroundColor: 'pageBackground',
          },
          [theme.breakpoints.down('md')]: {
            '& .MuiDialog-paper': {
              maxWidth: '70vw',
            },
          },
          [theme.breakpoints.down('sm')]: {
            '& .MuiDialog-paper': {
              maxWidth: '100vw',
              width: '100vw',
              height: '100vh',
            },
          },
        })}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <CompanyPaymentsPanel />
          <Box
            sx={theme => ({
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              display: 'none',
              pointerEvents: 'none',
              [theme.breakpoints.down('sm')]: {
                display: 'block',
              },
            })}
          >
            <Box
              sx={theme => ({
                position: 'relative',
                flex: 1,
                [theme.breakpoints.down('sm')]: {
                  display: 'flex',
                  padding: 2,
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                },
              })}
            >
              <Box
                sx={theme => ({
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0,
                  [theme.breakpoints.down('sm')]: {
                    zIndex: 1,
                    backgroundColor: 'pageBackground',
                    maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1), rgba(0,0,0,0))',
                  },
                })}
              />
              <IconButton
                onClick={onClose}
                sx={{
                  backgroundColor: 'white',
                  pointerEvents: 'visible',
                  zIndex: 1,
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </CompanyPaymentsDialogContext.Provider>
  )
}

export { useCompanyPaymentsDialog, CompanyPaymentsDialogProvider }
export default CompanyPaymentsDialogProvider
